import { FormattedMessage } from 'react-intl';
import { Box, Button, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { times } from 'lodash-es';

import {
  DEFAULT_MAX_UPLOADS_COUNT,
  DEFAULT_VALID_FILE_EXTENSIONS,
} from '@/features/file-attachments/config';

import { useFileAttachments, UseFileAttachmentsProps } from '../hooks/useFileAttachments';

import {
  FileAttachmentCard,
  FileAttachmentCardError,
  FileAttachmentCardPlaceholder,
  FileAttachmentUpload,
} from './FileAttachmentCard';

export type FileAttachmentCardsProps = {
  /** Component to use for the section heading text.  */
  HeadingComponent: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
} & Pick<
  Partial<UseFileAttachmentsProps>,
  | 'validFileExtensions'
  | 'allowFileUpload'
  | 'maxUploadsCount'
  | 'ocrFile'
  | 'uploadedFilesInitialValue'
  | 'onFieldChanged'
> &
  Pick<UseFileAttachmentsProps, 'callbacks'>;

export const FileAttachmentCards = ({
  HeadingComponent,
  allowFileUpload = true,
  callbacks,
  maxUploadsCount = DEFAULT_MAX_UPLOADS_COUNT,
  ocrFile,
  uploadedFilesInitialValue = [],
  validFileExtensions = DEFAULT_VALID_FILE_EXTENSIONS,
  onFieldChanged,
}: FileAttachmentCardsProps) => {
  const {
    attachments,
    attachmentsError,
    attachmentsIsLoading,
    refetchAttachments,
    prettyMaxFileSize,
    fileInputRef,
    uploadedFiles,
    isUploadingAttachments,
    deleteAttachment,
    handleAttachmentUpload,
    handleConfirmUpload,
    handleViewUploadedFile,
    handleViewAttachment,
    handleViewOcrAttachment,
    isMaxFilesUploaded,
    validFileTypes,
    displayConfirmButton,
  } = useFileAttachments({
    allowFileUpload,
    callbacks,
    maxUploadsCount,
    ocrFile,
    shouldFetchAttachments: true,
    uploadedFilesInitialValue,
    validFileExtensions,
    onFieldChanged,
  });

  function renderListItems() {
    if (attachmentsIsLoading) {
      return times(2, (key) => <FileAttachmentCardPlaceholder key={key} />);
    }

    if (attachmentsError) {
      return <FileAttachmentCardError refetch={refetchAttachments} />;
    }

    return (
      <>
        {ocrFile && (
          <FileAttachmentCard
            onView={() => handleViewOcrAttachment(ocrFile.documentFileId)}
            name={ocrFile.fileName}
          />
        )}
        {attachments?.map((attachment) => (
          <FileAttachmentCard
            key={attachment.companyFileStorageFileId ?? attachment.url ?? attachment.name}
            name={attachment.name}
            onView={() => handleViewAttachment(attachment)}
          />
        ))}
        {uploadedFiles?.map((file) => (
          <FileAttachmentCard
            key={file.companyFileStorageFileId ?? file.url}
            name={file.fileName}
            onView={
              file.companyFileStorageFileId
                ? () => handleViewUploadedFile(file)
                : () =>
                    handleViewAttachment({
                      name: file.fileName,
                      contentType: file.contentType,
                      url: file.url,
                    })
            }
            onDelete={file.companyFileStorageFileId ? () => deleteAttachment(file) : undefined}
          />
        ))}
        {allowFileUpload && (
          <FileAttachmentUpload
            fileInputRef={fileInputRef}
            validFileTypes={validFileTypes}
            handleAttachmentUpload={handleAttachmentUpload}
            isMaxFilesUploaded={isMaxFilesUploaded}
            isUploadingAttachments={isUploadingAttachments}
            maxUploadsCount={maxUploadsCount}
            prettyMaxFileSize={prettyMaxFileSize}
          />
        )}
      </>
    );
  }

  const hasNoListItems =
    !attachmentsIsLoading &&
    !attachmentsError &&
    ocrFile == null &&
    !attachments?.length &&
    !uploadedFiles?.length &&
    !allowFileUpload;

  return (
    <Box>
      <Typography component={HeadingComponent} variant="subtitle1" gutterBottom>
        <FormattedMessage defaultMessage="Attachments" />
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={3}>
        {renderListItems()}
      </Stack>
      {/* Temporary empty message until design thinks of something to put here. */}
      {hasNoListItems && (
        <Typography variant="body1" color="secondary">
          <FormattedMessage defaultMessage="There are no attachments." />
        </Typography>
      )}
      {displayConfirmButton && (
        <Button onClick={handleConfirmUpload} sx={{ mt: 2 }}>
          <FormattedMessage defaultMessage="Confirm Upload" />
        </Button>
      )}
    </Box>
  );
};

export type FileAttachmentCardsPlaceholderProps = Pick<
  FileAttachmentCardsProps,
  'HeadingComponent'
>;

export const FileAttachmentCardsPlaceholder = ({
  HeadingComponent,
}: FileAttachmentCardsPlaceholderProps) => (
  <Box>
    <Typography component={HeadingComponent} variant="subtitle1" gutterBottom>
      <FormattedMessage defaultMessage="Attachments" />
    </Typography>
    <Stack direction="row" flexWrap="wrap" gap={3}>
      {times(2, (key) => (
        <FileAttachmentCardPlaceholder key={key} />
      ))}
    </Stack>
  </Box>
);
